
































































































































































import Vue, { PropType } from "vue";
import moment from "moment";
import visibility from "@/utilities/visibility";

import {
  DeliveryDocumentModuleType,
  DocumentImage,
  DynamicDocumentGroup,
  DynamicDocumentItem,
  DynamicDocumentItemData,
  InstallationModule,
  InstallationModuleType,
  InstallationPlan,
  MechanicInstallation,
  InstallationModuleData
} from "@/types";
import { invertersMissing, InverterRecord } from "@/utilities/inverters";
import { SaveFailure } from "@/store/modules/saveStatus";
import { requestError, RequestError } from "@/utilities/request-error";

import LayoutPlan from "@/components/LayoutPlan";
import ModuleProgress from "@/components/Installation/ModuleProgress.vue";
import DialogModuleFinished from "./dialogs/ModuleFinished.vue";
import DynamicDocumentComp from "@/containers/dynamic-document";

export default Vue.extend({
  components: {
    DynamicDocument: DynamicDocumentComp,
    LayoutPlan,
    ModuleProgress,
    DialogModuleFinished
  },
  props: {
    installation: { type: Object as PropType<MechanicInstallation> },
    module: { type: Object as PropType<InstallationModule> },
    active: Boolean
  },

  data() {
    return {
      activePanels: [] as number[],
      lastError: null as RequestError | null,
      dialog: false,
      showMissingImagesDialog: false,
      showFinishedDialog: false,
      showUnsyncedImagesDialog: false,
      dialogTimeout: 0,
      syncTimeout: 0,
      cacheTimout: 0
    };
  },

  computed: {
    comment(): string {
      return this.installation.comment.trim() ?? "";
    },

    dynamicDocument(): DynamicDocumentGroup[] {
      return this.$store.getters["deliveryDocuments/dynamicDocument"](
        this.installation.id,
        this.module.type
      );
    },

    dynamicDocumentData(): DynamicDocumentItemData[] {
      return this.$store.getters["deliveryDocuments/dynamicDocumentData"](
        this.installation.id,
        this.module.type
      );
    },

    augmentedModuleData(): InstallationModuleData {
      const base: InstallationModuleData = { ...(this.module.data || {}) } as InstallationModuleData;

      if (base && typeof base.numberOfPanels === "number" && base.numberOfPanels > 0) {
        return base;
      }

      const panelsModule = (this.installation.modules || []).find(m => m.type === "PANELS-REGULAR");
      const fromPanels = panelsModule && panelsModule.data ? panelsModule.data.numberOfPanels : undefined;
      const fromScreening = this.installation && this.installation.screening
        ? this.installation.screening.numberOfPanels
        : undefined;

      return {
        ...base,
        numberOfPanels: base.numberOfPanels ?? fromPanels ?? fromScreening
      } as InstallationModuleData;
    },

    counterpartType(): DeliveryDocumentModuleType | null {
      const counterparts: Partial<Record<InstallationModuleType, DeliveryDocumentModuleType>> = {
        "PANELS-REGULAR": "METER-REGULAR",
        "METER-REGULAR": "PANELS-REGULAR"
      };

      return counterparts[this.module.type] ?? null;
    },

    invertersMissing(): boolean {
      const record = (type: DeliveryDocumentModuleType): InverterRecord => ({
        // This module carries the unsaved edits, the other one is as it was last saved.
        module:
          type === this.module.type
            ? this.module
            : this.installation.modules.find(m => m.type === type),
        answers: this.$store.getters["deliveryDocuments/data"](this.installation.id, type)
      });

      return invertersMissing(this.module.type, record("PANELS-REGULAR"), record("METER-REGULAR"));
    },

    moduleReady(): boolean {
      return !(
        this.invertersMissing ||
        this.missingItems.length > 0 ||
        this.dynamicDocument
          .flatMap(doc => doc.items)
          .some(
            item =>
              item.required &&
              !this.dynamicDocumentData.find(data => data.field === item.field)?.isValid
          )
      );
    },

    documentEnded(): boolean {
      return (
        this.$store.getters["deliveryDocuments/endedAt"](this.installation.id, this.module.type) > 0
      );
    },

    installationPlan(): InstallationPlan {
      return this.module.installationPlan!;
    },

    isPending(): boolean {
      return (
        this.$store.getters["installations/isPending"] ||
        this.$store.getters["installation/isPending"] ||
        this.$store.getters["deliveryDocuments/isPending"]
      );
    },

    lastUpdated(): number {
      const moduleUpdatedAt = this.$store.getters["installation/updatedAt"](
        this.installation.id,
        this.module.type
      );
      const documentUpdatedAt = this.$store.getters["deliveryDocuments/updatedAt"](
        this.installation.id,
        this.module.type
      );

      let updatedAt = 0;

      if (!Number.isNaN(moduleUpdatedAt) && moduleUpdatedAt > 0) {
        updatedAt = moduleUpdatedAt * 1000;
      }

      if (!updatedAt || (!Number.isNaN(documentUpdatedAt) && documentUpdatedAt > updatedAt)) {
        updatedAt = documentUpdatedAt;
      }

      return updatedAt;
    },

    saveFailure(): SaveFailure | undefined {
      return this.$store.getters["saveStatus/failure"](this.installation.id);
    },

    savedOffline(): boolean {
      return this.$store.getters["saveStatus/offline"](this.installation.id);
    },

    // The server's timestamp may be someone else's save; it is only the fallback.
    savedAt(): number {
      return this.$store.getters["saveStatus/savedAt"](this.installation.id) || this.lastUpdated;
    },

    lastSaved(): string {
      return this.savedAt > 0 ? moment(this.savedAt).calendar() : "nooit";
    },

    isSyncing(): boolean {
      return this.$store.getters["library/isSyncing"];
    },

    documentItems(): DynamicDocumentItem[] {
      return this.dynamicDocument.flatMap(doc => doc.items);
    },

    images(): DocumentImage[] {
      const items: DocumentImage[] = this.documentItems.flatMap(item => {
        const data = this.dynamicDocumentData.find(d => d.field === item.field)!;

        if (item.type === "IMAGE" && data.value instanceof Array) {
          return (data.value as string[]).map(url => this.createImage(url, item));
        }

        if (item.type === "SIGNATURE" && data.value instanceof Array) {
          return [this.createImage(data.value[1] as string, item)];
        }

        return [];
      });

      return items.filter(item => !!item.url);
    },

    localItems(): DocumentImage[] {
      return this.images.filter(item => !!item.media);
    },

    serverItems(): DocumentImage[] {
      return this.images.filter(item => !item.media);
    },

    missingServerItems(): DocumentImage[] {
      return this.serverItems.filter(item =>
        this.$store.getters["library/missingOnServer"](item.url)
      );
    },

    cachedServerItems(): DocumentImage[] {
      return this.serverItems.filter(
        item =>
          item.serverStatus === "missing" &&
          this.$store.getters["library/availableInCache"](item.url)
      );
    },

    unsyncedImages(): DocumentImage[] {
      return this.localItems.filter(item => !item.media!.saved && !item.media!.fromCache);
    },

    missingItems(): DocumentImage[] {
      return this.localItems.filter(
        item => !item.media!.saved || item.serverStatus !== "available"
      );
    }
  },

  watch: {
    unsyncedImages: {
      immediate: true,
      handler(items: DocumentImage[], prev?: DocumentImage[]) {
        if (!!prev && !!items.length && items.length > prev.length) {
          this.syncLibrary();
        }
      }
    },

    active: {
      immediate: true,
      async handler(val: boolean) {
        this.showFinishedDialog = false;
        window.clearTimeout(this.dialogTimeout);

        if (!val) return;

        await this.loadTemplate();
        await this.loadCounterpartDocument();

        this.dialogTimeout = window.setTimeout(async () => {
          if (!this.active) return;

          this.showFinishedDialog =
            this.active && (!!this.module.ended || this.documentEnded) && !this.installation.ended;
        }, 300);

        await this.verifyImages();
        await this.createImagesFromCache();
      }
    }
  },

  async mounted() {
    this.lastError = null;

    this.$store.dispatch("installation/poll:resume", {
      id: this.installation.id,
      moduleType: this.module.type
    });
  },

  created() {
    visibility.listen(this.handleVisibilityChange);
  },

  destroyed() {
    visibility.unlisten(this.handleVisibilityChange);
  },

  methods: {
    createImage(url: string, item: DynamicDocumentItem): DocumentImage {
      return {
        url,
        item,
        media: this.$store.getters["library/findItem"](url),
        serverStatus: this.$store.getters["library/serverStatus"](url)
      };
    },

    handleVisibilityChange(visible: boolean) {
      if (visible && this.active) {
        this.sync();
      }
    },

    async saveDocument() {
      await this.$store.dispatch("deliveryDocuments/save", {
        moduleType: this.module.type,
        id: this.installation.id
      });
    },

    endDocument() {
      return this.$store.dispatch("deliveryDocuments/end", {
        moduleType: this.module.type,
        id: this.installation.id
      });
    },

    async saveModule() {
      await this.$store.dispatch("installation/save", {
        moduleType: this.module.type,
        id: this.installation.id
      });
    },

    loadTemplate() {
      return this.$store.dispatch("deliveryDocuments/fetch", {
        id: this.installation.id,
        moduleType: this.module.type
      });
    },

    // The other module's answers decide, together with this one's, whether an inverter is on record.
    async loadCounterpartDocument() {
      const moduleType = this.counterpartType;

      if (!moduleType || !this.installation.modules.some(m => m.type === moduleType)) {
        return;
      }

      try {
        await this.$store.dispatch("deliveryDocuments/fetch", {
          id: this.installation.id,
          moduleType
        });
      } catch (e) {
        // Offline: go by what is in the store; the CRM checks again when the module is finished.
      }
    },

    async saveAndSend() {
      if (!this.moduleReady) {
        return;
      }

      this.lastError = null;

      try {
        await this.saveModule();
        await this.endDocument();

        await this.$store.dispatch("installation/end:module", {
          id: this.installation.id,
          moduleType: this.module.type
        });
        this.showFinishedDialog =
          (!!this.module.ended || this.documentEnded) && !this.installation.ended;
        this.$emit("ended");
      } catch (e) {
        this.lastError = requestError(e);
      }
    },

    async sync() {
      await this.loadTemplate();
      await this.verifyImages();
    },

    async verifyImages() {
      const items = this.images.filter(item => !item.media || !!item.media?.saved);

      await this.$store.dispatch(
        "library/verifyImages",
        items.map(item => item.url)
      );

      await this.getCacheItems();
    },

    async getCacheItems() {
      await this.$store.dispatch("library/getCacheItems");
    },

    syncLibrary(includeMissing: boolean = false) {
      if (this.isSyncing) {
        return;
      }

      window.clearTimeout(this.syncTimeout);

      this.syncTimeout = window.setTimeout(async () => {
        let items = includeMissing ? this.missingItems : this.unsyncedImages;

        await this.$store.dispatch(
          "library/sync",
          items.map(item => item.media)
        );

        await this.verifyImages();
      }, 500);
    },

    createImagesFromCache() {
      if (!this.cachedServerItems.length) return;

      window.clearTimeout(this.cacheTimout);

      this.cacheTimout = window.setTimeout(async () => {
        await this.$store.dispatch(
          "library/createMissingImagesFromCache",
          this.cachedServerItems.map(item => item.url)
        );
      }, 500);
    }
  }
});
